import { useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { getReactQueryAxiosInstance } from './api';
import { endpoints } from './endpoints';

export type ClaimWritebackStatus =
  | 'pending'
  | 'in_progress'
  | 'success'
  | 'error'
  | 'not_pushed';

export interface ClaimWritebackDocumentStatus {
  documentId: string;
  fileName: string;
  status: ClaimWritebackStatus;
  error?: string;
}

export interface ClaimWritebackStatusResponse {
  status: ClaimWritebackStatus;
  documents: ClaimWritebackDocumentStatus[];
  /** ISO date string returned by the backend when status === 'success' or 'error' */
  pushedAt?: string;
}

/**
 * Query key factory for writeback status
 */
export const writebackStatusKeys = {
  all: ['writeback-status'] as const,
  detail: (unifiedAppointmentId: string) =>
    [...writebackStatusKeys.all, unifiedAppointmentId] as const,
};

/**
 * React Query hook for polling writeback status by unifiedAppointmentId.
 *
 * Polls every 5 seconds while status is pending or in_progress.
 * Stops polling when status becomes success, error, or not_pushed,
 * or after ~2 minutes of stuck pending.
 *
 * @param unifiedAppointmentId - The appointment ID for status lookup (null to disable)
 * @returns React Query result with WritebackStatusResponse
 */
export const useWritebackStatus = (unifiedAppointmentId?: string | null) => {
  const pendingPollCount = useRef(0);

  // Reset counter when appointment changes
  useEffect(() => {
    pendingPollCount.current = 0;
  }, [unifiedAppointmentId]);

  const user = useSelector(
    (state: {
      login?: {
        user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
      };
    }) => state?.login?.user,
  );
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  return useQuery({
    queryKey: writebackStatusKeys.detail(unifiedAppointmentId ?? ''),
    queryFn: async (): Promise<ClaimWritebackStatusResponse> => {
      if (
        !unifiedAppointmentId ||
        !currentOrganizationId ||
        !currentPracticeId
      ) {
        throw new Error('Missing required parameters');
      }

      const instance = getReactQueryAxiosInstance();
      const response = await instance.request<ClaimWritebackStatusResponse>({
        method: 'GET',
        url: `/${endpoints.claimWritebackStatus}`,
        headers: {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
        },
        params: {
          unifiedAppointmentId,
        },
      });

      return response.data;
    },
    enabled: Boolean(
      unifiedAppointmentId && currentPracticeId && currentOrganizationId,
    ),
    refetchInterval: (data) => {
      // Terminal states — stop polling
      if (
        data?.status === 'success' ||
        data?.status === 'error' ||
        data?.status === 'not_pushed'
      ) {
        pendingPollCount.current = 0;
        return false;
      }
      // Actively processing — reset counter
      if (data?.status === 'in_progress') {
        pendingPollCount.current = 0;
      }
      pendingPollCount.current++;
      // Stop after ~2 min of pending (24 × 5s); reset so re-push starts fresh
      if (pendingPollCount.current > 24) {
        pendingPollCount.current = 0;
        return false;
      }
      return 5000;
    },
    staleTime: 0, // Always consider data stale to enable polling
  });
};
